import { useMemo } from "react";
import { useVendorPO } from "@/data/VendorPOContext";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import { resolveMaterialGate, MaterialGateVerdict } from "./materialGate";

/**
 * Hook that returns the material gate verdict for a given job.
 * @param jobId - The ID of the job to check.
 * @returns MaterialGateVerdict object.
 */
export function useMaterialGate(jobId: string): MaterialGateVerdict {
  const { purchaseOrders } = useVendorPO();
  const { packets } = useOrderingPacket();

  // Find the most recent packet for this job (by createdAt, descending) that is not cancelled.
  const jobPackets = useMemo(() => {
    return packets
      .filter((p) => p.jobId === jobId && p.status !== "cancelled")
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [packets, jobId]);

  const packet = jobPackets[0]; // most recent non-cancelled packet, or undefined if none.

  // We don't have a vendor reliability context, so we pass an empty record.
  // The resolveMaterialGate function defaults to 0.9 reliability for unknown vendors.
  const vendorReliability: Record<string, number> = {};

  // We can also pass a policy if we have one, but for now we use the default.
  const input = {
    jobId,
    purchaseOrders,
    packet,
    vendorReliability,
  };

  return resolveMaterialGate(input);
}